import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  BarChart,
  CartesianGrid,
  Legend,
  Bar as RechartsBar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { RefreshButton } from '../../elements/CustomButtons';
import {
  CustomStatCard,
  CustomXAxisTick,
} from '../../elements/CustomGraphElements';
import Error from '../../elements/Error';
import MultiSelectDropdown from '../../elements/MultiSelectDropdown';
import SmallSectionLoader from '../../elements/SmallSectionLoader';
import DefectAgingOrgMetrics from './DefectAgingOrgMetrics';

import { fetchDefectsLoggedAdminOverview } from '../../../modules/dashboard';
import QUERY_KEYS from '../../../query/queryKeys';
import ExportButton from '../../elements/common/export/ExportButton';
import { exportData } from '../../elements/common/export/exportUtils';

const TOTAL_COLOR = '#17a589';
const OPEN_COLOR = '#f39c12';
const REOPENED_COLOR = '#3c86e7ff';

const CustomTooltip = ({ active, payload, label, projectsMap }) => {
  if (!active || !payload?.length) return null;

  const projectName = projectsMap[label] || label;
  const isJiraIntegrated = !!payload[0]?.payload?.jiraIntegrated;

  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        padding: 10,
      }}>
      <p style={{ marginBottom: 6 }}>
        <strong>
          {projectName} ({label})
        </strong>
      </p>
      {!isJiraIntegrated ? (
        <p style={{ color: '#999', margin: 0 }}>Jira is not integrated</p>
      ) : (
        payload.map((entry) => (
          <p key={entry.dataKey} style={{ color: entry.color, margin: 0 }}>
            {entry.name}: {entry.value}
          </p>
        ))
      )}
    </div>
  );
};

const HomeDefectMetrics = ({ projectIds, pageError, showAccordion = true, page }) => {
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const [projectList, setProjectList] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [isSelectionInitialized, setIsSelectionInitialized] = useState(false);

  const defectProjectIdsForQuery = selectedProjectIds.length
    ? selectedProjectIds
    : projectIds;

  const {
    data: defectData,
    isLoading,
    isFetching,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: [QUERY_KEYS.dashboard.defectsOverview, defectProjectIdsForQuery],
    queryFn: async () => {
      const force = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return fetchDefectsLoggedAdminOverview(defectProjectIdsForQuery, force);
    },
    enabled: projectIds?.length > 0,  
  });

  useEffect(() => {
    if (
      isSuccess &&
      defectData?.data?.projects?.length &&
      !isSelectionInitialized
    ) {
      setProjectList(defectData.data.projects);
      setSelectedProjectIds(
        defectData.data.projects.map((p) => p.project_id)
      );
      setIsSelectionInitialized(true);
    }
  }, [isSuccess, defectData, isSelectionInitialized]);

  const projects = defectData?.data?.projects ?? [];
  const overall = defectData?.data?.overall ?? {};
  const refreshed_at = defectData?.refreshed_at ?? null;
  const projectsMap = useMemo(
    () =>
      projects.reduce(
        (map, p) => ({ ...map, [p.project_key]: p.project_name }),
        {}
      ),
    [projects]
  );

  const projectOptions = useMemo(() => {
    return projectList.map((p) => ({
      id: p.project_id,
      name: p.project_name,
    }));
  }, [projectList]);

  const projectData = useMemo(
    () =>
      projects.map((p) => ({
        key: p.project_key,
        name: p.project_name,
        totalDefects: p.total_defects ?? 0,
        openDefects: p.total_open_defects ?? 0,
        reopenedDefects: p.total_reopened_defects ?? 0,
        jiraIntegrated: p.is_jira_integrated || false,
      })),
    [projects]
  );

  if (isError || pageError) {
    return (
      <Error>
        Unable to fetch defect data. Please contact your administrator.
      </Error>
    );
  }

  const getSelectedProjectNamesForExport = () => {
    return projectList
      .filter((p) => selectedProjectIds.includes(p.project_id))
      .map((p) => p.project_name)
      .join(', ');
  };

  const buildDefectProjectRows = (projects) => {
    return projects.map((p) => ({
      'Project Key': p.project_key,
      'Project Name': p.project_name,
      'Total Defects': p.total_defects ?? 0,
      'Open Defects': p.total_open_defects ?? 0,
      'Reopened Defects': p.total_reopened_defects ?? 0,
      'Jira Integrated': p.is_jira_integrated ? 'Yes' : 'No',
    }));
  };

  const buildDefectAgingRows = (overallAging = []) => {
    return overallAging.map((bucket) => ({
      'Aging Category': bucket.category,
      'Total Defects': bucket.total ?? 0,
      ...Object.fromEntries(
        Object.entries(bucket).filter(([k]) => k !== 'category' && k !== 'total' && k !== 'jira_url')
      ),
    }));
  };

  const handleExportDefects = (projects, overallAging, type) => {
    const projectRows = buildDefectProjectRows(projects);
    const agingRows = buildDefectAgingRows(overallAging);

    const selectedProjects = getSelectedProjectNamesForExport();
    exportData({
      type,
      fileName: 'defect_metrics',
      sheets: [
        { name: 'Projects', data: projectRows },
        { name: 'Defect Aging', data: agingRows, metadata: `Project(s): ${selectedProjects}` },
      ],
    });
  };

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.dashboard.defectsOverview, defectProjectIdsForQuery],
      exact: true,
    });
  };

  const isSelectionExplicitlyEmpty =
    selectedProjectIds.length === 0 && isSelectionInitialized;

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: page !== 'dashboard' ? 'space-between' : 'flex-end',
          mb: 2,
          width: '100%',
        }}>
        {page !== 'dashboard' && (
          <>
            <Box sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}>
              <MultiSelectDropdown
                options={projectOptions}
                selectedValues={selectedProjectIds}
                placeholder="Select projects"
                onChange={setSelectedProjectIds}
                showSelectAll
                searchable
                name="projects"
              />
            </Box>
            <div className="d-flex gap-2 align-items-center">
              {!isSelectionExplicitlyEmpty && (
                <RefreshButton
                  onRefresh={handleRefresh}
                  isFetching={isFetching}
                  showLastUpdated
                  lastUpdated={refreshed_at}
                  buttonSize={24}
                  width={40}
                />
              )}
              <ExportButton
                tooltip="Export Defect Metrics"
                onExport={(type) =>
                  handleExportDefects(projects, overall?.defect_aging ?? [], type)
                }
                disabled={!projects.length}
              />
            </div>
          </>
        )}

      </Box>

      {isLoading || isFetching ? (
        <div style={{ minHeight: 500 }}>
          <SmallSectionLoader />
        </div>
      ) : (
        <>
          <Row>
            <Typography variant="h5" className="my-3">
              Defect Distribution
            </Typography>
            {page !== 'dashboard' && (
              <Col className="d-flex flex-column gap-2">
                <CustomStatCard
                  title="Total Defects"
                  count={overall.total_defects ?? 0}
                  isLoading={isLoading}
                />
                <CustomStatCard
                  title="Open Defects"
                  count={overall.total_open_defects ?? 0}
                  isLoading={isLoading}
                />
                <CustomStatCard
                  title="Reopened Defects"
                  count={overall.total_reopened_defects ?? 0}
                  isLoading={isLoading}
                />
              </Col>
            )}

            <Col
              md={10}
              className={`d-flex justify-content-center align-items-center ${page === 'dashboard' && `w-100`}`}
              style={{ height: 420 }}>
              <ResponsiveContainer>
                <BarChart
                  data={projectData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
                  barSize={25}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="key"
                    tick={(props) => (
                      <CustomXAxisTick {...props} projectsMap={projectsMap} />
                    )}
                    interval={0}
                    height={60}
                    label={{
                      value: 'Projects',
                      position: 'insideBottom',
                      fontSize: 14,
                    }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip
                    content={(props) => (
                      <CustomTooltip {...props} projectsMap={projectsMap} />
                    )}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <RechartsBar
                    dataKey="totalDefects"
                    fill={TOTAL_COLOR}
                    name="Total Defects"
                  />
                  <RechartsBar
                    dataKey="openDefects"
                    fill={OPEN_COLOR}
                    name="Open Defects"
                  />
                  <RechartsBar
                    dataKey="reopenedDefects"
                    fill={REOPENED_COLOR}
                    name="Reopened Defects"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Col>
          </Row>
          <DefectAgingOrgMetrics data={defectData} />
        </>
      )}
    </>
  );

  if (!showAccordion) {
    return (
      <div className="chart-div">
        <div style={{ width: '100%' }}>
          <Row className="align-items-center">
            <Col xs={3}></Col>
            <Col xs={6} className="text-center">
              <Typography variant="h5" className="dashboard-heading my-0">
                Defect Overview
              </Typography>
            </Col>
            <Col xs={3} className="d-flex justify-content-end gap-2">
              <RefreshButton
                onRefresh={handleRefresh}
                isFetching={isFetching}
                showLastUpdated
                lastUpdated={refreshed_at}
                buttonSize={24}
                width={40}
              />
              <ExportButton
                tooltip="Export Defect Metrics"
                onExport={(type) =>
                  handleExportDefects(projects, overall?.defect_aging ?? [], type)
                }
                disabled={!projects.length}
              />
            </Col>
          </Row>

          {content}
        </div>
      </div>
    );
  }

  return (
    <Accordion
      sx={{
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        marginBottom: '24px',
        backgroundColor: 'background.paper',
        '&:before': { display: 'none' },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: 'primary.main' }} />}
        sx={{
          backgroundColor: 'action.hover',
          borderRadius: '12px 12px 0 0',
          minHeight: '64px',
          '&.Mui-expanded': { minHeight: '64px' },
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            '&.Mui-expanded': { margin: '12px 0' },
          },
          '&:hover': { backgroundColor: 'action.selected' },
        }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Defect Overview
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          padding: '24px',
          borderRadius: '0 0 12px 12px',
        }}>
        {content}
      </AccordionDetails>
    </Accordion>
  );
};

export default HomeDefectMetrics;
